import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TdDialogService } from '@covalent/core/dialogs';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  share,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { USER } from 'src/app/core/user.service';
import { DutyLogHttpClient } from 'src/app/shared/flight-log-api/duty-logs/duty-log-client.service';
import { FlLoadingService } from 'src/lib/fl-loading/fl-loading.service';
import { AppState } from 'src/store';
import { DutyLogEntity, originatesFromGoldenHour } from 'src/store/duty-log/duty-log.model';
import { selectDutyLogById } from 'src/store/duty-log/duty-log.selectors';
import { sleep } from 'src/util/async';
import { tryParseDate } from 'src/util/dateLib';
import {
  getPageIndex,
  getPageQueryParams,
  getPageSize,
  tryGetSkipTakeOrDefault,
} from 'src/util/ui/pagination';
import { withoutNullOrEmptyValues } from 'src/util/values';
import { DutyLogEntryViewModel } from 'src/view-models/duty-log-entry-view-model';
import { UserViewModel } from 'src/view-models/user-view-model';

type DutyEvent = 'DutyOn' | 'DutyOff';

interface RouteParams {
  dutyOffStartTime?: string;
  dutyOffEndTime?: string;
  dutyOnStartTime?: string;
  dutyOnEndTime?: string;
  employeeId?: string;
  page?: string;
  pageSize?: string;
}

interface DutyHistoryParams {
  dutyOffStartTime?: Date;
  dutyOffEndTime?: Date;
  dutyOnStartTime?: Date;
  dutyOnEndTime?: Date;
  // dutyEvent: DutyEvent;
  employeeId?: string;
  skip: number;
  take: number;
  myHistoryOnly: boolean;
}

export function parseRouteParams(
  queryParams: RouteParams,
  defaultPageSize: number,
  user: UserViewModel,
): DutyHistoryParams {
  return {
    myHistoryOnly: user.isAviationSupervisor && queryParams.employeeId === user.id,
    // dutyEvent: queryParams.dutyEvent != null ? queryParams.dutyEvent : 'DutyOn',
    dutyOffStartTime: tryParseDate(queryParams.dutyOffStartTime),
    dutyOffEndTime: tryParseDate(queryParams.dutyOffEndTime),
    dutyOnStartTime: tryParseDate(queryParams.dutyOnStartTime),
    dutyOnEndTime: tryParseDate(queryParams.dutyOnEndTime),
    employeeId: user.isAviationSupervisor ? queryParams.employeeId : user.id,
    ...tryGetSkipTakeOrDefault(queryParams, defaultPageSize),
  };
}

function trySerializeDate(date?: Date) {
  if (date != null) {
    return format(date, 'YYYY-MM-DD');
  }
  return undefined;
}

function serializeFormValues(
  formParams: DutyHistoryParams & {
    dutyEvent?: DutyEvent;
    startTime?: Date;
    endTime?: Date;
  },
  user: UserViewModel,
): RouteParams {
  const dutyOnStartTime =
    formParams.dutyEvent === 'DutyOn' ? trySerializeDate(formParams.startTime) : undefined;
  const dutyOnEndTime =
    formParams.dutyEvent === 'DutyOn' ? trySerializeDate(formParams.endTime) : undefined;
  const dutyOffStartTime =
    formParams.dutyEvent === 'DutyOff' ? trySerializeDate(formParams.startTime) : undefined;
  const dutyOffEndTime =
    formParams.dutyEvent === 'DutyOff' ? trySerializeDate(formParams.endTime) : undefined;

  const routeParams: RouteParams = {
    dutyOnStartTime,
    dutyOnEndTime,
    dutyOffStartTime,
    dutyOffEndTime,
    employeeId: user.isAviationSupervisor ? formParams.employeeId : undefined,
  };

  return withoutNullOrEmptyValues(routeParams);
}

@Component({
  selector: 'fl-duty-history',
  templateUrl: './duty-history.component.html',
  styleUrls: ['./duty-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DutyHistoryComponent implements OnInit, OnDestroy {
  public defaultPageSize = 15;
  public pageSizes = [10, this.defaultPageSize, 50, 100];
  public page$ = new Subject<PageEvent>();
  public pageIndex$: Observable<number>;
  public pageSize$: Observable<number | undefined>;
  public total$: Observable<number>;
  public _pageRefresh$ = new BehaviorSubject<{}>({});

  public dutyLogEntries$: Observable<DutyLogEntryViewModel[]>;
  public reportParameters: UntypedFormGroup;
  public displayedColumns = ['dutyStartTime', 'dutyEndTime', 'employee', 'actions'];
  public _onDestroy$ = new Subject<void>();

  public loadingKey = 'duty-history-table';

  public displayDispatchedDutyLogsCtrl = new UntypedFormControl();
  public displayMyDutyLogsCtrl = new UntypedFormControl();

  get features() {
    return this._user.flightLogFeatures;
  }

  get isAviationSupervisor() {
    return this._user.isAviationSupervisor;
  }

  get companyCode() {
    return this._user.companycode;
  }

  get employeeIdCtrl(): UntypedFormControl {
    return this.reportParameters.get('employeeId') as UntypedFormControl;
  }

  get includeDispatched() {
    return this._user.isAviationSupervisor && this.displayDispatchedDutyLogsCtrl?.value === true;
  }

  public canDeleteHistory(dutyLog: DutyLogEntity) {
    return this._user.flightLogFeatures.deleteDutyHistory && !originatesFromGoldenHour(dutyLog);
  }

  constructor(
    @Inject(USER) private _user: UserViewModel,
    private _fb: UntypedFormBuilder,
    private _dutyLogs: DutyLogHttpClient,
    private _dialogService: TdDialogService,
    private _snackBar: MatSnackBar,
    private _route: ActivatedRoute,
    private _router: Router,
    private _store: Store<AppState>,
    private _loading: FlLoadingService,
  ) {
    const dutyLogsResults$ = this._route.queryParams.pipe(
      map((params) => parseRouteParams(params, this.defaultPageSize, this._user)),
      distinctUntilChanged(),
      // eslint-disable-next-line import/no-deprecated
      combineLatest(this._pageRefresh$),
      switchMap(([params]) => {
        this.displayMyDutyLogsCtrl.setValue(params.myHistoryOnly, {
          emitEvent: false,
        });

        let dutyEvent: DutyEvent | undefined;
        if (params.dutyOnStartTime != null || params.dutyOnEndTime != null) {
          dutyEvent = 'DutyOn';
        } else if (params.dutyOffStartTime != null || params.dutyOffEndTime != null) {
          dutyEvent = 'DutyOff';
        }

        this.reportParameters.patchValue(
          {
            dutyEvent: dutyEvent != null ? dutyEvent : 'DutyOn',
            startTime:
              dutyEvent === 'DutyOn'
                ? params.dutyOnStartTime
                : dutyEvent === 'DutyOff'
                ? params.dutyOffStartTime
                : null,
            endTime:
              dutyEvent === 'DutyOn'
                ? params.dutyOnEndTime
                : dutyEvent === 'DutyOff'
                ? params.dutyOffEndTime
                : null,
            employeeId: params.employeeId,
          },
          { emitEvent: false },
        );

        this.markAsLoading();
        return this._dutyLogs.getMany({
          companyId: this._user.companyId,
          includeDispatched: this.includeDispatched,
          pilotEmployeeId: params.employeeId,
          dutyOffStartTime: params.dutyOffStartTime,
          dutyOffEndTime: params.dutyOffEndTime,
          dutyOnStartTime: params.dutyOnStartTime,
          dutyOnEndTime: params.dutyOnEndTime,
          skip: params.skip,
          limit: params.take,
        });
      }),
      tap(() => this.markAsLoaded()),
      share(),
    );

    this.dutyLogEntries$ = dutyLogsResults$.pipe(map((r) => r.results));
    this.total$ = dutyLogsResults$.pipe(map((r) => r.totalCount));
  }

  public markAsLoading() {
    this._loading.register(this.loadingKey);
  }

  public markAsLoaded() {
    this._loading.resolveAll(this.loadingKey);
  }

  public ngOnInit() {
    this.reportParameters = this._fb.group({
      dutyEvent: 'DutyOn',
      startTime: null,
      endTime: null,
      employeeId: this._user.id,
    });

    this.reportParameters.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((value: DutyHistoryParams) => {
        const params = serializeFormValues(value, this._user);
        this._router.navigate(['duty-logs'], { queryParams: params });
      });

    this.pageIndex$ = getPageIndex(this._route);
    this.pageSize$ = getPageSize(this._route, this.defaultPageSize);
    this.page$.pipe(takeUntil(this._onDestroy$)).subscribe((page) => {
      this.patchQueryParams(getPageQueryParams(page, this.defaultPageSize));
    });

    this.displayMyDutyLogsCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((myLogs) => {
        this.employeeIdCtrl.setValue(myLogs != null ? this._user.id : null);
      });

    this.displayDispatchedDutyLogsCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        this._pageRefresh$.next({});
      });
  }

  public async delete(dutyLogEntry: DutyLogEntryViewModel) {
    const result = await this._dialogService
      .openConfirm({
        title: 'Delete Dutylog Entry?',
        message: 'Are you sure you wish to delete this entry?',
        acceptButton: 'Delete',
        cancelButton: 'Cancel',
      })
      .afterClosed()
      .toPromise();
    if (result === true) {
      try {
        await this._dutyLogs
          .delete({
            id: dutyLogEntry.id,
            pilotEmployeeId: dutyLogEntry.employeeId,
          })
          .toPromise();
        this.markAsLoading();
        // We're going to wait a couple seconds before showing a success message
        // and refreshing the page to give the read model a chance to catch up
        await sleep(2000);
        this._snackBar.open('Succesfully deleted duty log entry.', undefined, {
          duration: 2000,
        });
        this._pageRefresh$.next({});
      } catch (err) {
        this._snackBar.open('Error deleting duty log entry.', undefined, {
          duration: 2000,
        });

        throw err;
      }
    }
  }

  public getFromStore(dutyLog: DutyLogEntity) {
    return this._store.select(selectDutyLogById(dutyLog.id)).pipe(
      map((dl) => {
        return dl != null ? dl : dutyLog;
      }),
    );
  }

  public showEditShift(dutyLog: DutyLogEntryViewModel): Observable<boolean> {
    if (!this._user.flightLogFeatures.editDutyHistory || originatesFromGoldenHour(dutyLog)) {
      return of(false);
    }

    if (this._user.id === dutyLog.employeeId) {
      return this._user.latestShift$.pipe(
        map((latestShift) => {
          if (latestShift != null && latestShift.id === dutyLog.id) {
            if (latestShift.dutyPeriodEndTime == null || dutyLog.dutyPeriodEndTime == null) {
              // If the duty log in question is active and not finished, don't allow edit
              // Pilot should use duty off functionality
              return false;
            }
          }
          return true;
        }),
      );
    }

    return of(true);
  }

  public onPage(page: PageEvent): void {
    this.page$.next(page);
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public patchQueryParams<T extends {}>(params: T | undefined) {
    params = params != null ? params : ({} as any);
    const currentQueryparams = this._route.snapshot.queryParams;
    const queryParams = {
      ...currentQueryparams,
      ...params,
    };
    this._router.navigate(['duty-logs'], { queryParams });
  }

  public onClick(event: Event) {
    if (this.displayDispatchedDutyLogsCtrl?.value === true) {
      this.displayDispatchedDutyLogsCtrl.setValue(false, {
        emitEvent: true,
      });
    }
  }
}
